import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Modal, View, Text, Platform, AccessibilityInfo} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import NetInfo from '@react-native-community/netinfo';
import {RootState} from '../../../../shared/store';
import {
  hideErrorModal,
  setIsNetworkError,
  showErrorModal,
  confirmOvertimeYes,
  confirmOvertimeNo,
} from '../../../../shared/store/errorSlice';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {globalErrorModalStyles} from '../../../../shared/components/feedback/GlobalErrorModal/GlobalErrorModal.styles';
import {navigationRef} from '../../../../shared/navigation/navigationRef';
import {intentionalNavFlag} from '@shared/navigation/intentionalNavFlag';
import {networkErrorManager} from '@features/auth/api/axiosInstance';
import Button from '../../../../shared/components/ui/Button/Button';
import {setActiveTab, triggerHomeRefresh} from '@shared/store/menuSlice';
import {setSelectedRepairId} from '@shared/store/repairSlice';
import {menuTabs} from '@features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';

import {useTranslation} from 'react-i18next';
import TryAgain from '@shared/assets/icons/TryAgain';
import {hideLoader, showLoader} from '@shared/store/loaderSlice';
import {requestAuthenticate, pauseRepair} from '@features/repairDetails/RepairDetailsScreenController';
import {useIsZoomed} from '@shared/hooks/useSiteHeaderHeight';
import LoaderOverlay from '@shared/components/feedback/LoaderOverlay/LoaderOverlay';
import {getApiBaseUrl} from '@shared/config/agencyUrl';
import {buttonVariants} from '@shared/constants/global';
import {MODAL_SUPPORTED_ORIENTATIONS} from '@features/repairDetails/hooks/useSubmittingLoader';
import {errorHandler} from '@shared/constants/errorHandlers';
import { closeQuickAssistScreen } from '@shared/store/quickAssistSlice';

// ---------------------------------------------------------------------------
// Module-level helpers for handleAction — each handles one switch case body.
// ---------------------------------------------------------------------------

function handleDismissAction(subTitle: string | undefined, dispatch: any): void {
  networkErrorManager.rejectAllRequests({message: 'Request dismissed by user.'});
  dispatch(hideErrorModal());
  if (subTitle?.includes(errorHandler?.notInActivity)) {
    setTimeout(() => {
      dispatch(setActiveTab(menuTabs?.home as string));
      dispatch(setSelectedRepairId(''));
      dispatch(triggerHomeRefresh());
    }, 350);
  }
  if (subTitle?.includes(errorHandler?.alreadyInShift)) {
    setTimeout(() => {
      dispatch(setActiveTab(menuTabs?.home as string));
      dispatch(setSelectedRepairId(''));
      dispatch(triggerHomeRefresh());
    }, 350);
  }
}

async function handleRequestAuthorizationAction(
  cta: {label: string; type: string; primary?: boolean; payload?: Record<string, any>},
  selectedTechnician: any,
  createdUserId: any,
  screen: string | undefined,
  dispatch: any,
  t: any,
): Promise<void> {
  try {
    dispatch(showLoader());
    // The agency's own BFF host ({agency}.fastertech.ai), not a fixed one.
    const applicationBaseUrl = getApiBaseUrl();
    const result = await requestAuthenticate({
      documentId: cta.payload?.documentId ?? '',
      technicianId: selectedTechnician?.selectedTechnicianId ?? '',
      userId: createdUserId ?? '',
      navigateToHome: false,
      applicationBaseUrl,
      applicationVirtualPath: '',
    });
    dispatch(hideLoader());
    if (result.success) {
      dispatch(showErrorModal({
        title: t('createFlow.common.modalTitles.info'),
        subTitle: 'Authorization request sent successfully to',
        message: result.toEmail ?? '',
        noTranslation: true,
        underlineMessage: true,
        ctas: [{label: 'Ok', type: 'DISMISS'}],
        screen: screen ?? '',
      }));
    } else {
      dispatch(showErrorModal({
        title: t('repairDetails.authFailedTitle'),
        subTitle: result.message ?? '',
        message: '',
        ctas: [
          {label: t('repairDetails.cancelAuthorizationCTA'), type: 'CANCEL_SPENDING_LIMIT', primary: false},
          {label: t('repairDetails.requestAuthorizationAgainCTA'), type: 'REQUEST_AUTHORIZATION', payload: cta.payload},
        ],
        screen: screen ?? '',
        noTranslation: !!result.message,
      }));
    }
  } catch {
    dispatch(hideLoader());
  }
}

async function handleCancelSpendingLimitAction(
  selectedTechnician: any,
  userId: any,
  userTimeZone: string,
  dispatch: any,
  screen: string | undefined,
): Promise<void> {
  try {
    await pauseRepair({
      technicianId: Number(selectedTechnician?.selectedTechnicianId) || 0,
      userId: Number(userId) || 0,
      isLaborOvertimeCheckRequired: false,
      isPromptOvertimeCheckRequired: false,
      isWorkOrderSpendingLimitCheckRequired: false,
      userTimeZone,
    });
  } catch {
    // Ignore errors — navigate to dashboard regardless
  } finally {
    dispatch(hideErrorModal());
    setTimeout(() => {
      dispatch(setActiveTab(menuTabs?.home as string));
      dispatch(setSelectedRepairId(''));
      screen && dispatch(closeQuickAssistScreen())
    }, 350);
  }
}

async function handleRetryAction(
  screen: string | undefined,
  dispatch: any,
  t: any,
): Promise<void> {
  if (screen === 'backToAgencySelection') {
    dispatch(hideErrorModal());
    intentionalNavFlag.current = true;
    setTimeout(() => {
      navigationRef.current?.reset({index: 0, routes: [{name: 'AgencySelectionScreen'}]});
    }, 350);
    return;
  }
  const state = await NetInfo.fetch();
  if (state.isConnected) {
    try {
      dispatch(hideErrorModal());
      dispatch(showLoader());
      await networkErrorManager.retryAllRequests();
      dispatch(setIsNetworkError({isNetworkError: false}));
      dispatch(hideLoader());
    } catch (error) {
      console.error('Retry failed:', error);
    }
  } else {
    dispatch(setIsNetworkError({isNetworkError: true}));
    dispatch(showErrorModal({
      title: t('createFlow.common.modalTitles.error'),
      subTitle: t('common.networkError.subTitle'),
      message: t('common.networkError.desc'),
      ctas: [{label: t('common.networkError.tryAgaincta'), type: 'RETRY'}],
      screen: '',
      noTranslation: false,
    }));
  }
}

// ---------------------------------------------------------------------------
// Module-level sub-renders for renderSubTitleMessage to keep its CC ≤ 15.
// ---------------------------------------------------------------------------

function renderUnderlinedContent(
  subTitle: string,
  message: string,
  noTranslation: boolean | undefined,
  t: any,
  styles: any,
  modalSubTitleRef: React.MutableRefObject<any>,
): React.ReactNode {
  return (
    <Text
      ref={modalSubTitleRef}
      testID="global-error-modal-subTitle"
      style={styles.title}
      accessible={true}
      accessibilityRole="text"
      accessibilityLabel={`${noTranslation ? subTitle : t(subTitle)} ${noTranslation ? message : t(message)}`}
      {...(Platform.OS === 'web' ? {tabIndex: 0, id: MODAL_SUBTITLE_ID} : {})}>
      {noTranslation ? subTitle : t(subTitle)}{' '}
      <Text style={{textDecorationLine: 'underline'}}>
        {noTranslation ? message : t(message)}
      </Text>
    </Text>
  );
}

function renderStandardContent(
  subTitle: string | undefined,
  message: string | undefined,
  noTranslation: boolean | undefined,
  t: any,
  styles: any,
  modalSubTitleRef: React.MutableRefObject<any>,
): React.ReactNode {
  return (
    <>
      {subTitle && subTitle !== '' && (
        <Text
          ref={modalSubTitleRef}
          testID="global-error-modal-subTitle"
          style={styles.title}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={noTranslation ? subTitle : t(subTitle)}
          {...(Platform.OS === 'web' ? {tabIndex: 0, id: MODAL_SUBTITLE_ID} : {})}>
          {noTranslation ? subTitle : t(subTitle)}
        </Text>
      )}
      {message && message !== '' && (
        <Text
          testID="global-error-modal-message"
          style={styles.message}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={noTranslation ? message : t(message)}
          {...(Platform.OS === 'web' ? {tabIndex: 0} : {})}>
          {noTranslation ? message : t(message)}
        </Text>
      )}
    </>
  );
}

// Stable IDs for aria-labelledby / aria-describedby wiring on web.
const MODAL_TITLE_ID = 'global-error-modal-title';
const MODAL_SUBTITLE_ID = 'global-error-modal-subtitle';

/**
 * Moves screen-reader focus to a DOM element (web) or RN node (native).
 * findNodeHandle is lazy-required so it is never evaluated on the web bundle.
 */
function moveFocusToElement(ref: React.MutableRefObject<any>): void {
  if (!ref.current) {return;}

  if (Platform.OS === 'web') {
    // On web, ref.current is a real DOM element.
    const el = ref.current as HTMLElement;
    if (typeof el.focus === 'function') {
      // tabIndex="-1" makes the element programmatically focusable without
      // inserting it into the natural Tab order. Static text nodes should not
      // be Tab stops; screen readers read them via aria-labelledby / the dialog
      // role without requiring a tab stop.
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '-1');
      }
      el.focus({preventScroll: true});
    }
  } else {
    // Lazy require keeps findNodeHandle out of the web bundle evaluation path.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const {findNodeHandle} = require('react-native') as {
      findNodeHandle: (ref: any) => number | null;
    };
    const node = findNodeHandle(ref.current);
    if (node != null) {
      AccessibilityInfo.setAccessibilityFocus(node);
    }
  }
}

/**
 * Installs a keydown listener that traps Tab / Shift+Tab focus inside
 * `containerEl` while the modal is open.  Returns a cleanup function.
 */
function trapFocus(containerEl: HTMLElement): () => void {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const handler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') {return;}

    const focusable = Array.from(
      containerEl.querySelectorAll<HTMLElement>(focusableSelectors),
    ).filter(el => !el.closest('[aria-hidden="true"]'));

    if (focusable.length === 0) {return;}

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
}

function getCtaAccessibilityHint(type: string, t: (key: string) => string): string {
  if (type === 'RETRY') {
    return t('common.accessibility.globalErrorModal.retryHint');
  }
  if (type === 'DISMISS') {
    return t('common.accessibility.globalErrorModal.dismissHint');
  }
  if (type === 'NAVIGATE') {
    return t('common.accessibility.globalErrorModal.navigateHint');
  }
  return t('common.accessibility.globalErrorModal.performsActionHint');
}

const GlobalErrorModal = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const theme = useScreenTheme('globalErrorModal');
  const isZoomed = useIsZoomed();
  const styles = useMemo(
    () => globalErrorModalStyles(theme, isZoomed),
    [theme, isZoomed],
  );

  // Ref for the modal title — primary focus target when modal opens.
  const modalTitleRef = useRef<any>(null);
  // Ref for the subtitle — fallback focus target when there is no title.
  const modalSubTitleRef = useRef<any>(null);
  // Ref for the dialog container — used to install the focus trap on web.
  const modalContainerRef = useRef<any>(null);
  // Holds the focus-trap cleanup function so the effect can remove the listener.
  const trapCleanupRef = useRef<(() => void) | undefined>(undefined);

  // ── CANCEL_SPENDING_LIMIT in-flight state ──────────────────────────────────
  // Local state drives a LoaderOverlay rendered INSIDE the modal so it is
  // visible in the same native layer (the global loader renders at app root,
  // below the Modal layer — invisible when a Modal is open).
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  // Ref-based guard drops any extra taps that arrive before React commits the
  // disabled button state (e.g. during the setState→re-render window).
  const isCancelSpendingLimitInFlightRef = useRef(false);

  const {
    isVisible,
    title,
    subTitle,
    message,
    ctas,
    screen,
    noTranslation,
    isNetworkError,
    icon,
    underlineMessage,
  } = useSelector((state: RootState) => state.error);

  const {selectedTechnician, createdUserId} = useSelector(
    (state: RootState) => state.technician,
  );
  const {userDetails} = useSelector((state: RootState) => state.user);
  const userTimeZone =
    (userDetails?.keyName as string | undefined) ??
    userDetails?.displayName ??
    '';

  useEffect(() => {
    if (isVisible && Platform.OS !== 'android') {
      dispatch(hideLoader());
    }
  }, [isVisible]);

  // When a CANCEL_SPENDING_LIMIT call is in flight and the network drops,
  // handleNetworkError() queues the pauseRepair request and shows the
  // "No network connection" modal content.  At that point the
  // `await pauseRepair()` inside handleAction is stuck on a never-resolving
  // Promise (it only resolves when "Try again" retries it), so the finally
  // block that resets isCancelLoading never runs — leaving the spinner frozen
  // on top of the network error modal and blocking all user interaction.
  // Watching isNetworkError lets us reset the spinner as soon as the network
  // error modal takes over, so the "Try again" button becomes usable.
  useEffect(() => {
    if (isNetworkError && isCancelLoading) {
      setIsCancelLoading(false);
      isCancelSpendingLimitInFlightRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNetworkError]);

  // Track the element that had focus before the modal opened so we can
  // restore it when the modal closes (web only).
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Move accessibility focus into the modal whenever it becomes visible so
  // screen readers announce the dialog immediately without the user needing
  // to navigate to it first.
  useEffect(() => {
    if (!isVisible) {return;}

    if (Platform.OS === 'web') {
      // Save the currently-focused element so focus can be restored on close.
      previouslyFocusedRef.current =
        (typeof document === 'undefined' ? null : document.activeElement as HTMLElement);
    }

    // 300 ms delay lets the fade-in animation finish before shifting focus,
    // preventing a race condition on iOS/Android.
    const timer = setTimeout(() => {
      if (Platform.OS === 'web') {
        // On web: prefer to focus the dialog container itself first so the
        // screen reader announces it as a dialog, then let natural Tab order
        // move the user to the title / buttons.
        if (modalContainerRef.current) {
          const el = modalContainerRef.current as HTMLElement;
          if (!el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
          }
          el.focus({preventScroll: true});
        } else {
          // Fallback: focus title or subtitle text.
          const targetRef =
            title && title !== '' ? modalTitleRef : modalSubTitleRef;
          moveFocusToElement(targetRef);
        }
      } else {
        // Native: move VoiceOver / TalkBack focus to title or subtitle.
        const targetRef =
          title && title !== '' ? modalTitleRef : modalSubTitleRef;
        moveFocusToElement(targetRef);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [isVisible, title]);

  // Install a focus trap on web so Tab / Shift+Tab cannot leave the dialog.
  // aria-modal="true" handles screen-reader background exclusion; we do NOT
  // use the inert attribute on body siblings because the RN Web modal portal
  // is appended asynchronously and the timing makes it impossible to reliably
  // identify the portal root — any mistake marks the modal itself inert and
  // freezes the entire application.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) {return;}

    let trapCleanup: (() => void) | undefined;

    // Use requestAnimationFrame to ensure the modal portal is fully mounted
    // in the DOM before we query focusable elements inside it.
    const rafId = requestAnimationFrame(() => {
      if (modalContainerRef.current) {
        trapCleanupRef.current = trapFocus(modalContainerRef.current as HTMLElement);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (typeof trapCleanupRef.current === 'function') {
        trapCleanupRef.current();
        trapCleanupRef.current = undefined;
      }
      // Restore focus to the element that was focused before the modal opened.
      if (previouslyFocusedRef.current &&
          typeof previouslyFocusedRef.current.focus === 'function') {
        previouslyFocusedRef.current.focus({preventScroll: true} as any);
        previouslyFocusedRef.current = null;
      }
    };
  }, [isVisible]);

  // Delegates each switch case to a module-level handler to keep CC ≤ 15.
  const handleAction = async (cta: {
    label: string;
    type: string;
    primary?: boolean;
    payload?: Record<string, any>;
  }) => {
    switch (cta.type) {
      case 'DISMISS':
        handleDismissAction(subTitle, dispatch);
        break;
      case 'YES':
        dispatch(confirmOvertimeYes());
        dispatch(hideErrorModal());
        break;
      case 'NO':
        dispatch(confirmOvertimeNo());
        dispatch(hideErrorModal());
        break;
      case 'NAVIGATE':
        dispatch(hideErrorModal());
        if (screen === 'repairDetails') {
          setTimeout(() => {
            dispatch(setActiveTab(menuTabs?.repairs as string));
            dispatch(setSelectedRepairId(''));
          }, 350);
        }
        break;
      case 'REQUEST_AUTHORIZATION':
        await handleRequestAuthorizationAction(cta, selectedTechnician, createdUserId, screen, dispatch, t);
        break;
      case 'RETRY':
        await handleRetryAction(screen, dispatch, t);
        break;
      case 'CANCEL_SPENDING_LIMIT':
        if (isCancelSpendingLimitInFlightRef.current) {break;}
        isCancelSpendingLimitInFlightRef.current = true;
        setIsCancelLoading(true);
        try {
          await handleCancelSpendingLimitAction(selectedTechnician, createdUserId, userTimeZone, dispatch, screen);
        } finally {
          isCancelSpendingLimitInFlightRef.current = false;
          setIsCancelLoading(false);
        }
        break;
      default:
        dispatch(hideErrorModal());
    }
  };

  const renderModalHeader = () => {
    if (!title || title === '') {return null;}
    const webTitleProps =
      Platform.OS === 'web' ? {tabIndex: 0, id: MODAL_TITLE_ID} : {};
    return (
      <View style={styles.headerTitleContainer}>
        {icon && (
          <View
            style={styles.iconContainer}
            accessible={false}
            importantForAccessibility="no"
            {...(Platform.OS === 'web' ? ({'aria-hidden': 'true'} as any) : {})}>
            {icon}
          </View>
        )}
        <Text
          ref={modalTitleRef}
          style={styles.headerTitle}
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel={noTranslation ? title : t(title)}
          // The stable id is referenced by aria-labelledby on the dialog.
          {...webTitleProps}
          testID="modalHeader">
          {noTranslation ? title : t(title)}
        </Text>
      </View>
    );
  };

  // Delegates to module-level helpers to keep renderSubTitleMessage CC ≤ 15.
  const renderSubTitleMessage = () => (
    <>
      {underlineMessage && subTitle && message
        ? renderUnderlinedContent(subTitle, message, noTranslation, t, styles, modalSubTitleRef)
        : renderStandardContent(subTitle, message, noTranslation, t, styles, modalSubTitleRef)}
    </>
  );

  const renderButtons = () => (
    <View style={styles.buttonContainer}>
      {ctas?.map(cta => (
        <Button
          key={cta.type}
          testID={`global-error-modal-cta-${cta.type}`}
          title={noTranslation ? cta.label : t(cta.label)}
          onPress={() => handleAction(cta)}
          disabled={cta.type === 'CANCEL_SPENDING_LIMIT' && isCancelLoading}
          fullWidth={false}
          variant={
            cta.primary === false
              ? buttonVariants.secondary
              : buttonVariants.primary
          }
          showLeftIcon
          customLeftIcon={isNetworkError && <TryAgain />}
          customIconStyles={styles.buttonContainerTextStyles}
          containerStyle={cta.primary === false && styles.cancelContainerStyle}
          customTextStyle={
            cta.primary === false && styles.cancelCustomTextStyle
          }
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={
            noTranslation ? cta.label : t((cta?.label ?? '') as string)
          }
          accessibilityHint={getCtaAccessibilityHint(cta.type, t)}
        />
      ))}
    </View>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return Platform?.OS === 'web' ? (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      supportedOrientations={MODAL_SUPPORTED_ORIENTATIONS}
      accessibilityViewIsModal={true}>
      <View style={styles.container}>
        <View
          ref={modalContainerRef}
          style={styles.modal}
          accessible={true}
          importantForAccessibility="yes"
          {...({
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': title && title !== '' ? MODAL_TITLE_ID : undefined,
            'aria-describedby':
              subTitle && subTitle !== '' ? MODAL_SUBTITLE_ID : undefined,
            // tabIndex="-1" makes the container programmatically focusable
            // (so we can call .focus() on it to announce the dialog to screen
            // readers) without inserting it into the natural Tab order.
            tabIndex: -1,
            // Data attribute used by lockBackgroundInert as a fallback selector
            // to identify the modal portal when modalContainerRef is unavailable.
            'data-global-error-modal': 'true',
          } as any)}>
          <View style={styles.header}>{renderModalHeader()}</View>
          <View style={styles.titleContainer}>
            <View style={styles.titleSubContainer}>
              {renderSubTitleMessage()}
            </View>
          </View>
          {renderButtons()}
          {isCancelLoading && <LoaderOverlay insideModal visible />}
        </View>
      </View>
    </Modal>
  ) : (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      supportedOrientations={MODAL_SUPPORTED_ORIENTATIONS}
      accessibilityViewIsModal={true}>
      <View style={styles.container}>
        <SafeAreaView style={styles.modal} accessible={false}>
          <View style={styles.header}>{renderModalHeader()}</View>
          <View style={styles.titleContainer}>
            <View style={styles.titleSubContainer} accessible={false}>
              {renderSubTitleMessage()}
            </View>
          </View>
          {renderButtons()}
          {isCancelLoading && <LoaderOverlay insideModal visible />}
        </SafeAreaView>
      </View>
    </Modal>
  );
};

export default GlobalErrorModal;
